import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { useSearchLogic } from "./useSearchLogic";
import { SearchTrigger } from "./SearchTrigger";
import { SearchInput } from "./SearchInput";
import { MobileSearchDrawer } from "./MobileSearchDrawer";
import { BaseSearchProps } from "./types";
import Translations from "@translate/components/Portfolio/Search/PortfolioTabSearch";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks/useIsPc";
import { usePortfolioContext } from "@/src/components/Portfolio/PortfolioContext";

const I18n = I18nUtil.createScoped(Translations);

/**
 * PortfolioTabSearch
 * - Tabs 右侧搜索入口（Basic UI Component）
 * - 需求要点：
 *   1) idle：仅图标；hover：图标自右向左滑动，文案"search your watchlist"透明度 0→1 渐显
 *   2) active：点击后展示输入框（placeholder: "Search"）与右侧 Cancel 按钮
 *   3) 输入后展示下拉，空结果显示 Empty（apps/token-seek/src/components/Empty.tsx）
 * - 使用 fetchFolderContents 获取收藏夹内的资产
 */
type PortfolioTabSearchProps = BaseSearchProps;

export const PortfolioTabSearch: React.FC<PortfolioTabSearchProps> = memo(
  ({ onSelect, onClear, portfolioId }) => {
    const { searchAsset, searchInputActive, setSearchInputActive, keywords } =
      usePortfolioContext();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    // 不再需要独立 hovered 状态，交由 CSS group-hover 与 expanded 协同控制
    const transitionMs = 200;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const desktopActiveRef = useRef<HTMLDivElement | null>(null);
    const [isPc] = useIsPc();

    const {
      showDropdown,
      setShowDropdown,
      results,
      loading,
      hasMore,
      loadMore,
      handleInputChange,
      handleSelect,
      handleCloseDropdown,
      clearSearch,
    } = useSearchLogic({ onSelect, onClear, portfolioId });

    // 只在点击触发器时聚焦输入框
    const handleTriggerClick = useCallback(() => {
      // 桌面端由 CSS hover + 点击进入输入；移动端使用 Drawer
      if (!isPc) {
        setMobileOpen(true);
      } else {
        setSearchInputActive(true);
        setShowDropdown(true);
      }
      // 给一点延迟确保 input 已经渲染
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }, [setShowDropdown, isPc]);

    const handleCancel = useCallback(() => {
      // 先播放淡出动画，再收起
      setClosing(true);
      window.setTimeout(() => {
        setSearchInputActive(false);
        clearSearch();
        setClosing(false);
        // 交互由 CSS 控制，无需额外 hovered 状态
      }, transitionMs);
    }, [transitionMs, clearSearch, setClosing]);

    const handleMobileCancel = useCallback(() => {
      clearSearch();
      setMobileOpen(false);
    }, [clearSearch]);

    // 桌面端：点击外部区域关闭 dropdown（模仿菜单点击外部关闭的行为）
    useEffect(() => {
      if (!searchInputActive || !isPc) {
        return;
      }

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node | null;
        if (
          desktopActiveRef.current &&
          target &&
          !desktopActiveRef.current.contains(target)
        ) {
          handleCloseDropdown();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [searchInputActive, isPc, handleCloseDropdown]);

    if (isPc && searchInputActive) {
      return (
        <div ref={desktopActiveRef}>
          <SearchInput
            keywords={keywords}
            onChange={handleInputChange}
            onCancel={handleCancel}
            inputRef={inputRef}
            closing={closing}
            showDropdown={showDropdown}
            results={results}
            loading={loading}
            onSelect={handleSelect}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        </div>
      );
    }

    return (
      <>
        <div className="relative">
          {/* 桌面端交互：hover 展开 + 点击进入输入态 */}
          <SearchTrigger
            data-openreplay-label="Search · PortfolioTabSearch · SearchTrigger"
            onClick={handleTriggerClick}
            expanded={searchInputActive}
          />

          {/* 移动端交互：点击打开 DrawerH5，无 hover 动画 */}
          <button
            data-openreplay-label="Open search Button"
            id="btn-open-search"
            type="button"
            className="md:hidden w-5 h-9 flex items-center justify-center ml-3"
            onClick={handleTriggerClick}
            aria-label="Open search"
          >
            <Icon name="search" size={20} className="text-content_tertiary" />
          </button>

          <MobileSearchDrawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            keywords={keywords}
            onChange={handleInputChange}
            onCancel={handleMobileCancel}
            inputRef={inputRef}
            results={results}
            loading={loading}
            onSelect={(asset) => {
              handleSelect(asset);
              setMobileOpen(false);
            }}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        </div>
        {/* 移动端且有搜索数据时显示底部清除按钮 */}
        {!isPc && !!searchAsset && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-background_solid_secondary ">
            <button
              aria-label="Portfolio Tab Search"
              data-openreplay-label={`Portfolio Tab Search Button | ${I18n.t("Return to all collections")}`}
              id="btn-portfolio-tab-search"
              type="button"
              className="button tonal_button md_button w-full text-content_primary"
              onClick={clearSearch}
            >
              {I18n.t("Return to all collections")}
            </button>
          </div>
        )}
      </>
    );
  },
);

PortfolioTabSearch.displayName = "PortfolioTabSearch";
